import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  FileButton,
  Flex,
  Group,
  Image,
  List,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import {
  IconEdit,
  IconPhoto,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useApp } from "../../../context/AppContext";
import {
  formatDimensions,
  formatFileSize,
  getImageFormatLabel,
  ImageDimensions,
  readImageDimensions,
} from "../../../utils/imageFileMetadata";
import styles from "./BrandingTab.module.css";

// Per the design: SVG, PNG, JPG only, max 2 MB. The frontend is intentionally
// stricter than the SSG endpoint so users get an inline error before upload.
const ACCEPTED_LOGO_MIME = [
  "image/svg+xml",
  "image/png",
  "image/jpeg",
  "image/jpg",
];
const ACCEPT_ATTR = ".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg";
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const MAX_LOGO_SIZE_LABEL = "2 MB";
const LOGO_MAX_HEIGHT = 168;

export interface BrandingTabProps {
  businessInfo: any;
  editing: boolean;
  readOnly?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  /**
   * Called after a successful save (upload or removal) so the parent can refresh
   * the salon record (to pick up the new logo_url) and leave edit mode.
   */
  onSaved: () => void | Promise<void>;
}

const validateLogoFile = (file: File): string | null => {
  const acceptedByExt = /\.(svg|png|jpe?g)$/i.test(file.name);
  if (!ACCEPTED_LOGO_MIME.includes(file.type) && !acceptedByExt) {
    return "Unsupported file type. Use SVG, PNG, or JPG.";
  }
  if (file.size > MAX_LOGO_SIZE_BYTES) {
    return `Image is too large. Maximum size is ${MAX_LOGO_SIZE_LABEL}.`;
  }
  return null;
};

const descriptionFor = (editing: boolean, hasSavedLogo: boolean): string => {
  if (editing)
    return "Review the details below, replace or remove the image, then save.";
  if (hasSavedLogo)
    return "Logo is uploaded and available to be used across the platform.";
  return "Add your business logo to display across your booking page, emails and printable assets.";
};

/**
 * Branding subtab of My Business — a self-contained read/edit surface for the
 * business logo with four states: empty (upload), review (a freshly staged file
 * with its details, before saving), saved (the current logo), and edit (replace
 * or remove the saved logo). Uploads and removals commit only on Save; the
 * preview and file details are read entirely client-side, so Cancel never
 * touches the backend. See `onSaved` for the post-save refresh handoff.
 */
export default function BrandingTab({
  businessInfo,
  editing,
  readOnly = false,
  onEdit,
  onCancel,
  onSaved,
}: BrandingTabProps) {
  const { uploadImage, removeSalonLogo } = useApp();
  const resetRef = useRef<() => void>(null);

  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [stagedDimensions, setStagedDimensions] =
    useState<ImageDimensions | null>(null);
  const [pendingRemove, setPendingRemove] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentLogoUrl: string | null = businessInfo?.logo_url ?? null;
  const hasStagedChange = stagedFile !== null || pendingRemove;

  // Revoke the object URL when it changes or the component unmounts.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Read intrinsic dimensions of the staged file (async, client-side).
  useEffect(() => {
    if (!stagedFile) {
      setStagedDimensions(null);
      return;
    }
    let cancelled = false;
    readImageDimensions(stagedFile).then((dims) => {
      if (!cancelled) setStagedDimensions(dims);
    });
    return () => {
      cancelled = true;
    };
  }, [stagedFile]);

  const clearStaged = () => {
    setStagedFile(null);
    setPreviewUrl(null);
    setStagedDimensions(null);
    setError(null);
    resetRef.current?.();
  };

  const handleFile = (file: File | null) => {
    if (!file) return;
    const validationError = validateLogoFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setStagedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setPendingRemove(false);
    setError(null);
    if (!editing) onEdit();
  };

  const handleRemoveLogo = () => {
    // A staged file "removes" by discarding the staged selection; an existing
    // saved logo stages a removal that commits on Save.
    if (stagedFile) {
      clearStaged();
      return;
    }
    if (currentLogoUrl) {
      setPendingRemove(true);
      setError(null);
    }
  };

  const handleCancel = () => {
    clearStaged();
    setPendingRemove(false);
    onCancel();
  };

  const handleSave = async () => {
    if (!hasStagedChange || !businessInfo?.id) return;
    const removing = pendingRemove && !stagedFile;
    try {
      setSaving(true);
      setError(null);
      if (removing) {
        await removeSalonLogo(businessInfo.id);
      } else if (stagedFile) {
        await uploadImage({
          image: stagedFile,
          business_location_id: businessInfo.id,
          image_type: "salon_logo",
          optimization_type: "web",
        });
      }
      clearStaged();
      setPendingRemove(false);
      await onSaved();
    } catch (err) {
      console.error("Error saving logo:", err);
      setError(
        removing
          ? "Couldn't remove the logo. Please try again."
          : "Logo upload failed. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const frameClass = [styles.logoFrame, styles.clickable].join(" ");

  const replaceTrigger = (src: string, alt: string) => (
    <FileButton onChange={handleFile} accept={ACCEPT_ATTR} resetRef={resetRef}>
      {(props) => (
        <UnstyledButton
          {...props}
          className={frameClass}
          aria-label="Replace logo"
        >
          <Image
            src={src}
            alt={alt}
            fit="contain"
            mah={LOGO_MAX_HEIGHT}
            w="auto"
          />
          <Box className={styles.overlay}>
            <Box component="span" className={styles.overlayLabel}>
              <IconUpload size={16} /> Click to replace
            </Box>
          </Box>
        </UnstyledButton>
      )}
    </FileButton>
  );

  const renderEmpty = () => {
    if (readOnly) {
      return (
        <Box className={styles.uploadCard} style={{ cursor: "default" }}>
          <Box className={styles.uploadIconWrap}>
            <IconPhoto size={24} />
          </Box>
          <Text size="sm" c="dimmed">
            No logo uploaded yet
          </Text>
        </Box>
      );
    }
    return (
      <FileButton
        onChange={handleFile}
        accept={ACCEPT_ATTR}
        resetRef={resetRef}
      >
        {(props) => (
          <UnstyledButton
            {...props}
            className={styles.uploadCard}
            aria-label="Upload your logo"
          >
            <Box className={styles.uploadIconWrap}>
              <IconUpload size={24} />
            </Box>
            <Text fw={600} c="var(--brand-teal)">
              Click to upload your logo
            </Text>
            <Text size="sm" c="dimmed">
              SVG, PNG or JPG · max {MAX_LOGO_SIZE_LABEL}
            </Text>
          </UnstyledButton>
        )}
      </FileButton>
    );
  };

  const renderSaved = () => {
    if (readOnly || !currentLogoUrl) {
      return (
        <Box className={styles.logoFrame}>
          <Image
            src={currentLogoUrl ?? undefined}
            alt="Business logo"
            fit="contain"
            mah={LOGO_MAX_HEIGHT}
            w="auto"
          />
        </Box>
      );
    }
    return (
      <UnstyledButton
        className={frameClass}
        onClick={onEdit}
        aria-label="Edit logo"
      >
        <Image
          src={currentLogoUrl}
          alt="Business logo"
          fit="contain"
          mah={LOGO_MAX_HEIGHT}
          w="auto"
        />
        <Box className={styles.overlay}>
          <Box component="span" className={styles.overlayLabel}>
            <IconEdit size={16} /> Edit Image
          </Box>
        </Box>
      </UnstyledButton>
    );
  };

  const renderEditPreview = () => {
    if (previewUrl) return replaceTrigger(previewUrl, "Selected logo preview");
    if (pendingRemove) {
      return (
        <Box className={styles.logoFrame}>
          <Stack className={styles.removePlaceholder} align="center" gap={6}>
            <IconPhoto size={28} />
            <Text size="sm">Logo will be removed on Save</Text>
          </Stack>
        </Box>
      );
    }
    if (currentLogoUrl) return replaceTrigger(currentLogoUrl, "Business logo");
    return renderEmpty();
  };

  const renderDetails = () => {
    if (!stagedFile) return null;
    const rows: Array<[string, string]> = [
      ["File name", stagedFile.name],
      ["Format", getImageFormatLabel(stagedFile)],
      ["Dimensions", formatDimensions(stagedDimensions)],
      ["File size", formatFileSize(stagedFile.size)],
    ];
    return (
      <Box className={styles.detailsBox}>
        {rows.map(([label, value]) => (
          <Box key={label} className={styles.detailRow}>
            <Text component="span" className={styles.detailLabel}>
              {label}
            </Text>
            <Text component="span" className={styles.detailValue}>
              {value}
            </Text>
          </Box>
        ))}
      </Box>
    );
  };

  const renderEditing = () => {
    const showRemove =
      !readOnly &&
      (stagedFile !== null || (!!currentLogoUrl && !pendingRemove));
    return (
      <Flex
        gap="xl"
        direction={{ base: "column", sm: "row" }}
        align="flex-start"
      >
        <Stack gap="sm" w={{ base: "100%", sm: 260 }}>
          {renderEditPreview()}
          {showRemove && (
            <Button
              variant="outline"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleRemoveLogo}
              disabled={saving}
              fullWidth
            >
              Remove logo
            </Button>
          )}
        </Stack>

        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>
          {renderDetails()}
          <List spacing={6} size="sm" c="dimmed">
            <List.Item>Acceptable formats: SVG, PNG, JPG</List.Item>
            <List.Item>Maximum file size: {MAX_LOGO_SIZE_LABEL}</List.Item>
            <List.Item>Recommended: square, at least 512 × 512 px</List.Item>
          </List>
        </Stack>
      </Flex>
    );
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4}>
          <Group gap="sm" align="center">
            <Title order={4}>Logo</Title>
            {editing && hasStagedChange && (
              <Badge color="#DE854E" variant="light" radius="sm" size="sm">
                NOT SAVED
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed">
            {descriptionFor(editing, !!currentLogoUrl)}
          </Text>
        </Stack>

        {editing && (
          <Group gap="sm" wrap="nowrap">
            <Button
              variant="subtle"
              color="gray"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              onClick={handleSave}
              loading={saving}
              disabled={!hasStagedChange}
            >
              Save
            </Button>
          </Group>
        )}
      </Group>

      {!editing && !currentLogoUrl && renderEmpty()}
      {!editing && currentLogoUrl && renderSaved()}
      {editing && renderEditing()}

      {error && (
        <Text size="sm" c="red" role="alert">
          {error}
        </Text>
      )}
    </Stack>
  );
}
